import { useMemo } from "react";
import { Permission } from "./permissions";

type UserPermission = {
	id: string;
	user_id: string;
	organisation_id: string | null;
	type: Permission;
};

type OrgObject = {
	id: string;
	broker_id?: string | null;
	agent_id?: string | null;
	holder_id?: string | null;
};
type OidInput =
	| string
	| string[]
	| OrgObject
	| OrgObject[]
	| "none"
	| "any"
	| null;

const resolveOrgIds = (oid: OidInput): string[] | null => {
	if (oid === null || oid === "none" || oid === "any") {
		return null;
	}
	const items = Array.isArray(oid) ? oid : [oid];
	return items.flatMap((item) => {
		if (!item) return [];
		if (typeof item === "string") return [item];
		const ids: string[] = [item.id];
		if (item.broker_id) ids.push(item.broker_id);
		if (item.agent_id) ids.push(item.agent_id);
		if (item.holder_id) ids.push(item.holder_id);
		return ids;
	});
};

const check =
	(permissions: UserPermission[], uwr: boolean) =>
	(
		oid: OidInput = "none",
		typ: Permission | Permission[],
		checkUwr: boolean = true,
		checkSealord: boolean = true
	) => {
		if (checkUwr && uwr) {
			// allow uwr access to this permission check
			return true;
		}

		if (
			checkSealord &&
			permissions.some((p) => p.type === Permission.SEALORD)
		) {
			// allow sealord access to this permission check
			return true;
		}

		const permissionTypes = Array.isArray(typ) ? typ : [typ];
		const organisationIds = resolveOrgIds(oid);

		return permissions.some((permission) => {
			// skip if we're not checking for this type
			const typeMatches = permissionTypes.includes(permission.type);
			if (!typeMatches) {
				return false;
			}

			// "none" or null - no organisation (global)
			if (oid === null || oid === "none") {
				return permission.organisation_id === null;
			}

			// "any" - any organisations
			if (oid === "any") {
				return permission.organisation_id !== null;
			}

			return organisationIds.includes(permission.organisation_id || "");
		});
	};

const useUserPermissions = (permissions?: UserPermission[], uwr?: boolean) => {
	const checkPermissions = useMemo(
		() => check(permissions || [], uwr || false),
		[permissions, uwr]
	);
	return {
		checkPermissions,
	};
};

export default useUserPermissions;
